import { createFileRoute } from '@tanstack/react-router'
import { GuideArticle, GuideSection } from '~/components/guide/GuideArticle'

export const Route = createFileRoute('/guide/concepts')({
  component: Concepts,
})

function Concepts() {
  return (
    <GuideArticle>
      <GuideSection title="Package layout">
        <ul className="list-disc space-y-1 pl-5">
          <li><code>packages/shared</code> — isomorphic zod schemas, types, numbering, typed HTTP errors. No runtime deps.</li>
          <li><code>packages/db</code> — Drizzle schema, Postgres client, migrations, seed.</li>
          <li><code>packages/api</code> — REST library: PAT/OIDC auth, the <code>authed()</code> wrapper, shared data-access, S3 presign, OpenAPI registry.</li>
          <li><code>packages/mcp</code> — MCP server generated from the same OpenAPI route registry.</li>
          <li><code>packages/web</code> — React SPA + TanStack Start server. REST adapters under <code>routes/api/v1</code>; RPC server functions under <code>src/server</code>.</li>
        </ul>
      </GuideSection>

      <GuideSection title="Adding an entity">
        <p>Follow the same path the <code>users</code> feature takes through every layer:</p>
        <ol className="list-decimal space-y-1 pl-5">
          <li>Add a zod schema in <code>packages/shared/src/schemas</code>.</li>
          <li>Add a Drizzle table in <code>packages/db/src/schema.ts</code>, then <code>db:generate</code> + <code>db:migrate</code>.</li>
          <li>Add shared data-access in <code>packages/api</code> (used by both REST and RPC).</li>
          <li>Add a REST route under <code>routes/api/v1</code> with <code>authed()</code> + <code>registerRoute()</code> — MCP tools generate automatically.</li>
          <li>Add a <code>createServerFn</code> RPC wrapper in <code>src/server</code> and a page that calls it.</li>
        </ol>
      </GuideSection>

      <GuideSection title="Auth & permissions">
        <p>
          Browser users sign in via Keycloak (OIDC). API and MCP clients send
          either a personal access token (<code>cbp_</code>), an admin-minted
          application token (<code>cba_</code>), or a Keycloak bearer token
          obtained through OAuth (what Claude Chat / Cowork connectors use —
          see MCP Instructions). A bearer token resolves to the same user and
          role as a web sign-in, creating the account on first contact if
          needed. Every request resolves to a permission set — a user's role
          permissions or a token's scopes — and{' '}
          <code>authed({'{ scope }'})</code> gates each route. The vocabulary
          lives in <code>permissionSchema</code>; extend it as you add resources.
          Roles and application keys choose from the same list, so a key can
          be given any permission a role can, such as <code>eta:read</code> on
          its own for an integration that only reads ETAs.
        </p>
      </GuideSection>
    </GuideArticle>
  )
}
